import AbstractConfig from './AbstractConfig';
import {IListConfig} from '../../@interfaces/components/List/IListConfig';
import {IInteractiveListConfig} from '../../@interfaces/services/ConfigMaker/IInteractiveListConfig';
import getUri from '../../helpers/getUri';
import app from '../../helpers/app';
import {EndpointUri} from '../../@types/EndpointUri';
import {ListConfigColumn, ListConfigNamedColumn} from '../../@types/components/List';

export default class ListConfig extends AbstractConfig<IListConfig & {
    controllerName: string
}> implements IInteractiveListConfig {
    protected filterableColumns: Record<string, ListConfigNamedColumn> | null = null;
    protected searchableColumns: Record<string, ListConfigNamedColumn> | null = null;

    getPreparedColumnsList<Column extends ListConfigColumn = ListConfigColumn>(
        columns: Record<string, Column>,
        preferredColumnsVisibility?: Record<string, boolean> | null,
        preferredColumnsOrder?: string[] | null,
    ): ListConfigNamedColumn[] {
        const result: ListConfigNamedColumn[] = [];

        const columnNameToIndexMap: Record<string, number> = {};
        const forceColumnsVisibility: Record<string, boolean> = preferredColumnsVisibility ?? {};

        for (const [name, column] of Object.entries(columns)) {
            const forceVisibility = forceColumnsVisibility[name];
            if (
                !this.isPermittedColumn(column) ||
                !this.isForceVisibleColumn(column) && (
                    forceVisibility == null && !this.isVisibleColumn(column) ||
                    forceVisibility != null && !forceVisibility
                )
            ) {
                continue;
            }

            columnNameToIndexMap[name] = result.push({...column, name}) - 1;
        }

        if (!preferredColumnsOrder) {
            return result;
        }

        const orderedList = preferredColumnsOrder.reduce<ListConfigNamedColumn[]>((res, name) => {
            if (!columnNameToIndexMap.hasOwnProperty(name)) {
                return res;
            }

            res.push(result[columnNameToIndexMap[name]]);
            delete columnNameToIndexMap[name];

            return res;
        }, []);

        // push rest of columns to end of list
        Object.values(columnNameToIndexMap).forEach(index => orderedList.push(result[index]));

        return orderedList;
    }

    getConfig(): IListConfig & { controllerName: string } {
        return this.getAttributes();
    }

    getUrlOfModelListPage(): string {
        return this.guessUrlOfModelListPage();
    }

    getUrlOfModelShowPage(modelId?: number): string {
        return this.resolverPageUri(this.getConfig().update?.url, modelId) || this.guessUrlOfModelShowPage(modelId);
    }

    getUrlOfModelCreatePage(): string {
        return this.resolverPageUri(this.getConfig().create?.url) || this.guessUrlOfModelCreatePage();
    }

    getUrlOfRecordsListEndpoint(): string {
        return getUri(
            this.getConfig().getListUri
        ) || this.guessUrlOfRecordsListEndpoint();
    }

    guessUrlOfModelListPage(): string {
        return getUri({
            route: this.namesResolver.clientRoutes.getIndexRouteName(this.getConfig().controllerName)
        });
    }

    guessUrlOfModelShowPage(modelId?: number): string {
        const params = this.getModelShowPageRouteParams(modelId);
        const controllerName = this.getConfig().controllerName;
        const showRoute = {
            route: this.namesResolver.clientRoutes.getShowRouteName(controllerName),
            params
        };

        if (modelId != null) {
            return getUri(showRoute);
        }

        return getUri({
            route: this.namesResolver.clientRoutes.getCreateRouteName(controllerName)
        }, showRoute);
    }

    guessUrlOfModelCreatePage(): string {
        return getUri({
            route: this.namesResolver.clientRoutes.getCreateRouteName(this.getConfig().controllerName),
            params: {id: 'create'}
        });
    }

    guessUrlOfRecordsListEndpoint(): string {
        return getUri({
            route: this.namesResolver.apiRoutes.getIndexRouteName(this.getConfig().controllerName)
        }, {
            route: this.namesResolver.apiRoutes.getIndexRouteName()
        });
    }

    getColumnLabel(column: ListConfigColumn | string): string {
        const prefix = this.getAttribute('columnsNameL10NPrefix') ?? '';
        const config = this.getColumnConfig(column);
        const name = config != null && ('name' in config) ?
            String(config['name']) :
            (typeof column === 'string' ? column : '');

        return prefix + (config?.label ?? name);
    }

    getColumnFilterLabel(column: ListConfigColumn | string): string {
        const prefix = this.getAttribute('columnsNameL10NPrefix') ?? '';
        const config = this.getColumnConfig(column);
        const name = config != null && 'name' in config ?
            String(config['name']) :
            (typeof column === 'string' ? column : '');

        return prefix + (config?.filter?.label ?? config?.label ?? name);
    }

    getSearchableColumns(): Record<string, ListConfigNamedColumn> {
        return this.searchableColumns ??= this.getFilteredColumns(this.isSearchableColumn.bind(this));
    }

    getFilterableColumns(): Record<string, ListConfigNamedColumn> {
        return this.filterableColumns ??= this.getFilteredColumns(this.isFilterableColumn.bind(this));
    }

    hasFilterableColumns(): boolean {
        return Object.keys(this.getFilterableColumns()).length > 0;
    }

    hasSearchableColumns(): boolean {
        return Object.keys(this.getSearchableColumns()).length > 0;
    }

    isPermittedColumn(column: ListConfigColumn | string): boolean {
        const columnConfig = this.getColumnConfig(column);
        if (!columnConfig || !columnConfig.permissions) {
            return columnConfig !== null;
        }

        return app('auth').getUser()?.canAny(columnConfig.permissions) ?? false;
    }

    isVisibleColumn(column: ListConfigColumn | string): boolean {
        const config = this.getColumnConfig(column);

        return config !== null && (this.isForceVisibleColumn(config) || config.invisible !== true);
    }

    isForceVisibleColumn(column: ListConfigColumn | string): boolean {
        const config = this.getColumnConfig(column);

        return config !== null && config.forceVisible === true;
    }

    isSearchableColumn(column: ListConfigColumn | string): boolean {
        const config = this.getColumnConfig(column);

        return config !== null && config.searchable === true;
    }

    isFilterableColumn(column: ListConfigColumn | string): boolean {
        const config = this.getColumnConfig(column);
        if (config != null && 'name' in config && config.name === 'id') {
            return false;
        }

        return config !== null && config.filter?.enabled === true;
    }

    protected getColumnConfig(column: ListConfigColumn | string): ListConfigColumn | null {
        if (typeof column !== 'string') {
            return column;
        }

        const columns = this.getConfig().columns;
        if (!columns.hasOwnProperty(column)) {
            return null;
        }

        return columns[column];
    }

    protected getModelShowPageRouteParams(modelId?: number): Record<'id', string> {
        return {id: modelId != null ? String(modelId) : 'create'};
    }

    protected resolverPageUri(uri?: EndpointUri, modelId?: number): string | null {
        let pageUri: string | null = null;

        if (typeof uri === 'string') {
            const routeParams = this.getModelShowPageRouteParams(modelId);

            if (uri.includes(':')) {
                pageUri = app('url').generate(uri, routeParams);
            } else {
                pageUri = app('url').route(uri, routeParams) ?? uri;
            }
        } else if (uri) {
            pageUri = app('url').route(uri.route, uri.params);
        }

        return pageUri;
    }

    protected getFilteredColumns(filter: (column: ListConfigColumn, name: string) => boolean): Record<string, ListConfigNamedColumn> {
        const res: Record<string, ListConfigNamedColumn> = {};
        for (const [name, column] of Object.entries(this.getConfig().columns)) {
            if (!filter(column, name)) {
                continue;
            }

            const namedColumn = column as ListConfigNamedColumn;
            if (!namedColumn.name) {
                namedColumn.name = name;
            }

            res[name] = namedColumn;
        }

        return res;
    }

    protected get namesResolver() {
        return app('names-resolver');
    }
}
